import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { SrIcon } from "@/components/ui/SrIcon";
import { toast } from "sonner";
import {
  useSystemConfigString,
  useUpdateSystemConfigString,
  type SystemConfigKey,
} from "@/hooks/use-system-config";

interface FieldDef {
  key: SystemConfigKey;
  label: string;
  placeholder: string;
  multiline?: boolean;
  type?: "text" | "email" | "tel" | "url";
  colspan?: 1 | 2;
}

const FIELDS: FieldDef[] = [
  { key: "company_name", label: "Firmanavn", placeholder: "Sølvrev Produksjoner AS", colspan: 2 },
  { key: "company_org_nr", label: "Org-nr", placeholder: "NO 123 456 789 MVA", colspan: 1 },
  { key: "company_phone", label: "Telefon", placeholder: "+47 12 34 56 78", type: "tel", colspan: 1 },
  { key: "company_address", label: "Adresse", placeholder: "Gateadresse 1", multiline: true, colspan: 2 },
  { key: "company_postal_code", label: "Postnr", placeholder: "0123", colspan: 1 },
  { key: "company_city", label: "Sted", placeholder: "Oslo", colspan: 1 },
  { key: "company_email", label: "E-post", placeholder: "post@solvrev.no", type: "email", colspan: 1 },
  { key: "company_website", label: "Nettside", placeholder: "https://solvrev.no", type: "url", colspan: 1 },
];

/**
 * Admin-UI for bedriftsdetaljer på `/innstillinger/organisasjon`. Verdier
 * lagres i `system_config` med `company_*`-prefiks. Brukes ikke aktivt av
 * eksisterende kode ennå (PPTX/faktura kobler inn separat når aktuelt).
 */
export function CompanyDetailsCard() {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <SrIcon name="building" className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-display text-base">Bedriftsdetaljer</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Brukes på faktura, PPTX-eksport og e-postutsending. La stå tomt for å
          skjule feltet i eksport.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {FIELDS.map((f) => (
            <CompanyField key={f.key} field={f} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function CompanyField({ field }: { field: FieldDef }) {
  const { data: stored = "" } = useSystemConfigString(field.key);
  const update = useUpdateSystemConfigString();
  const [value, setValue] = useState(stored);

  // Sync når verdien laster eller endres av annen bruker
  useEffect(() => setValue(stored), [stored]);

  const dirty = value !== stored;

  async function save() {
    try {
      await update.mutateAsync({ key: field.key, value });
      toast.success(`${field.label} lagret`);
    } catch (err) {
      toast.error(`Kunne ikke lagre: ${(err as Error).message}`);
    }
  }

  return (
    <div className={field.colspan === 2 ? "sm:col-span-2" : undefined}>
      <Label htmlFor={field.key} className="text-xs">
        {field.label}
      </Label>
      <div className="flex gap-2 mt-1">
        {field.multiline ? (
          <Textarea
            id={field.key}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={() => {
              if (dirty) save();
            }}
            placeholder={field.placeholder}
            rows={2}
            className="text-sm flex-1"
          />
        ) : (
          <Input
            id={field.key}
            type={field.type ?? "text"}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={() => {
              if (dirty) save();
            }}
            placeholder={field.placeholder}
            className="text-sm flex-1"
          />
        )}
        {dirty && (
          <Button
            size="sm"
            variant="outline"
            onClick={save}
            disabled={update.isPending}
            className="shrink-0"
          >
            Lagre
          </Button>
        )}
      </div>
    </div>
  );
}
